import { Dispatch } from 'react';
import { Template } from 'store/reducer/templates';
import { STRINGS } from '../../config';
import { createSectionHeadings } from './templates';

export function markdownToBullets(text: string): string {
  // see tests for expected behaviour
  const boldHeadingSectionRegex = /\*\*(.+?)\*\*\s*\n([\s\S]*?)(?=(\n\*\*|$))/g;
  const result: string[] = [];

  const matches = Array.from(text.matchAll(boldHeadingSectionRegex));

  if (matches.length === 0) {
    // If no markdown headings found, return the text as-is
    return text;
  }

  matches.forEach(match => {
    const heading = `**${match[1].trim()}**`;
    const content = match[2]
      .trim()
      .split(/(?<=[.!?])\s+/)
      .filter(s => s.length > 0)
      .map(s => `- ${s.trim()}`)
      .join('\n');

    result.push(`${heading}\n${content}`);
  });

  return result.join('\n\n');
}

const defaultPracticeRole = 'Clinician';
const defaultSectionHeadings = [
  'Presenting Complaint',
  'History of Presenting Complaint',
  'Past Medical History Discussed',
  'Drug History Discussed',
  'Social History',
  'Concerns and expectations',
  'Investigations',
  'Treatment',
  'Advice',
];

const {
  SUMMARISER: { HOST },
  REDUCERS: {
    SUMMARY: { START_SUMMARY_REQUEST, GET_SUMMARY, ERROR_SUMMARY_REQUEST, UPDATE_SUMMARY },
  },
} = STRINGS;

export async function startSummary(
  userId: string,
  consultationId: string,
  transcription: string,
  dispatch: Dispatch<unknown>,
  template: Template,
  isTemplateFlagOn: boolean,
  selectedPracticeId: string,
): Promise<void> {
  dispatch({ type: START_SUMMARY_REQUEST, payload: { userId, consultationId } });
  const { practiceRole, content, transcriptionType } = template;
  const sectionHeadings = createSectionHeadings(content);
  try {
    const response = await fetch(`${HOST}/generate/summary?practiceId=${selectedPracticeId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: isTemplateFlagOn
        ? JSON.stringify({ transcription, practiceRole, sectionHeadings, consultationId, transcriptionType })
        : JSON.stringify({
            transcription,
            practiceRole: defaultPracticeRole,
            sectionHeadings: defaultSectionHeadings,
            consultationId,
            transcriptionType,
          }),
    });
    if (response.ok) {
      const { summary, title } = await response.json();
      const bulletPointSummary = summary ? markdownToBullets(summary) : summary;

      const payload = {
        summary: bulletPointSummary,
        ...(title !== undefined && { title }),
      };

      dispatch({ type: GET_SUMMARY, payload });
      return;
    }

    if (response.status === 204) {
      dispatch({ type: GET_SUMMARY, payload: { summary: null, title: null } });
    }
  } catch (error) {
    dispatch({ type: ERROR_SUMMARY_REQUEST, payload: { error, consultationId } });
  }
}

export const updateSummary = (summary: string) => ({
  type: UPDATE_SUMMARY,
  payload: { summary },
});
